/**
 * Runtime Monitor for WasteMap High-Performance SPA.
 * Monitors and aggregates system-level UX performance metrics:
 * - FPS (rolling average via high-precision requestAnimationFrame)
 * - Navigation/Route Transition times
 * - Tab Switch times
 * - Session Restore times
 * - Cache Hit / Miss counters
 * - Reloads and React Tree Reconstructions prevented
 * - Component Render counts
 */

export interface RuntimeMetrics {
  fps: number;
  avgNavigationTime: number;
  slowestPage: string;
  avgRestoreTime: number;
  cacheHits: number;
  cacheMisses: number;
  reloadsPrevented: number;
  reactReconstructionsPrevented: number;
  renders: number;
}

class RuntimeMonitor {
  private fpsValues: number[] = [];
  private lastFrameTime = typeof window !== 'undefined' ? performance.now() : 0;
  private currentFps = 60;
  
  // High-precision timing maps
  private navigationTimes: Record<string, number[]> = {};
  private restoreTimes: number[] = [];
  private tabSwitchTimes: number[] = [];

  // Core counter stats (saved in sessionStorage to persist across soft reloads if needed,
  // but reset on brand new sessions)
  private cacheHits = 0;
  private cacheMisses = 0;
  private reloadsPrevented = 0;
  private reactReconstructionsPrevented = 0;
  private renders = 0;

  private listeners = new Set<(metrics: RuntimeMetrics) => void>();

  constructor() {
    if (typeof window !== 'undefined') {
      this.initFpsTracker();
      this.loadPersistedCounters();
    }
  }

  private loadPersistedCounters() {
    try {
      this.cacheHits = parseInt(sessionStorage.getItem('wm_rm_cache_hits') || '0', 10);
      this.cacheMisses = parseInt(sessionStorage.getItem('wm_rm_cache_misses') || '0', 10);
      this.reloadsPrevented = parseInt(sessionStorage.getItem('wm_rm_reloads_prevented') || '0', 10);
      this.reactReconstructionsPrevented = parseInt(sessionStorage.getItem('wm_rm_reconstructions_prevented') || '0', 10);
      this.renders = parseInt(sessionStorage.getItem('wm_rm_renders') || '0', 10);
    } catch {
      // Graceful fallback
    }
  }

  private persistCounters() {
    try {
      sessionStorage.setItem('wm_rm_cache_hits', this.cacheHits.toString());
      sessionStorage.setItem('wm_rm_cache_misses', this.cacheMisses.toString());
      sessionStorage.setItem('wm_rm_reloads_prevented', this.reloadsPrevented.toString());
      sessionStorage.setItem('wm_rm_reconstructions_prevented', this.reactReconstructionsPrevented.toString());
      sessionStorage.setItem('wm_rm_renders', this.renders.toString());
    } catch {
      // Graceful fallback
    }
  }

  private initFpsTracker() {
    const calcFps = () => {
      const now = performance.now();
      const delta = now - this.lastFrameTime;
      this.lastFrameTime = now;
      
      const fps = Math.min(60, Math.round(1000 / delta));
      this.fpsValues.push(fps);
      if (this.fpsValues.length > 60) {
        this.fpsValues.shift();
      }

      this.currentFps = Math.round(
        this.fpsValues.reduce((sum, val) => sum + val, 0) / this.fpsValues.length
      );

      requestAnimationFrame(calcFps);
    };

    requestAnimationFrame(calcFps);
  }

  // Record a page/route navigation transition duration
  public recordNavigation(page: string, durationMs: number) {
    if (!this.navigationTimes[page]) {
      this.navigationTimes[page] = [];
    }
    this.navigationTimes[page].push(durationMs);
    this.notify();
  }

  // Record a tab-switch duration
  public recordTabSwitch(durationMs: number) {
    this.tabSwitchTimes.push(durationMs);
    this.notify();
  }

  // Record a session restoration duration
  public recordRestore(durationMs: number) {
    this.restoreTimes.push(durationMs);
    this.notify();
  }

  // Counter Increments
  public incrementCacheHit() {
    this.cacheHits++;
    this.persistCounters();
    this.notify();
  }

  public incrementCacheMiss() {
    this.cacheMisses++;
    this.persistCounters();
    this.notify();
  }

  public incrementReloadsPrevented() {
    this.reloadsPrevented++;
    this.persistCounters();
    this.notify();
  }

  public incrementReactReconstructionsPrevented() {
    this.reactReconstructionsPrevented++;
    this.persistCounters();
    this.notify();
  }

  public incrementRenders() {
    this.renders++;
    this.persistCounters();
    this.notify();
  }

  // Metric Retrievals
  public getMetrics(): RuntimeMetrics {
    // Calc average navigation time across all pages
    let totalNavTime = 0;
    let totalNavCount = 0;
    let slowestPage = 'Nenhum';
    let maxAvgTime = 0;

    Object.entries(this.navigationTimes).forEach(([page, times]) => {
      const sum = times.reduce((s, t) => s + t, 0);
      const avg = sum / times.length;
      totalNavTime += sum;
      totalNavCount += times.length;

      if (avg > maxAvgTime) {
        maxAvgTime = avg;
        slowestPage = page;
      }
    });

    const avgNavigationTime = totalNavCount > 0 ? totalNavTime / totalNavCount : 0;

    // Calc average restore time
    const avgRestoreTime = this.restoreTimes.length > 0 
      ? this.restoreTimes.reduce((s, t) => s + t, 0) / this.restoreTimes.length 
      : 0;

    return {
      fps: this.currentFps,
      avgNavigationTime,
      slowestPage,
      avgRestoreTime,
      cacheHits: this.cacheHits,
      cacheMisses: this.cacheMisses,
      reloadsPrevented: this.reloadsPrevented,
      reactReconstructionsPrevented: this.reactReconstructionsPrevented,
      renders: this.renders
    };
  }

  // Live Diagnostic Logging
  public logMount(name: string) {
    console.count(`${name} Mounted`);
    console.log(`%c[Runtime Inspector] ${name} Mounted (Total Mounts logged above)`, "color: #10B981; font-weight: bold;");
  }

  public logUnmount(name: string) {
    console.log(`%c[Runtime Inspector] ${name} Unmounted`, "color: #EF4444; font-weight: bold;");
  }

  public logRender(name: string, count: number) {
    console.log(`%c[Runtime Inspector] ${name} Rendered (${count})`, "color: #3B82F6;");
  }

  public logProviderRecreation(name: string) {
    console.warn(`[Runtime Inspector] Provider Recreated: ${name}`);
  }

  public logContextRecreation(name: string) {
    console.warn(`[Runtime Inspector] Context Recreated: ${name}`);
  }

  // Subscription pipeline for real-time manager updates
  public subscribe(callback: (metrics: RuntimeMetrics) => void): () => void {
    this.listeners.add(callback);
    callback(this.getMetrics()); // Immediate initial call
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notify() {
    const metrics = this.getMetrics();
    this.listeners.forEach(listener => {
      try {
        listener(metrics);
      } catch (err) {
        console.error('[RuntimeMonitor] Subscription listener error:', err);
      }
    });
  }
}

export const runtimeMonitor = new RuntimeMonitor();

// React hook to auto-inspect and track component lifecycles in real-time
import { useEffect, useRef } from 'react';

export function useRuntimeInspector(componentName: string) {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  useEffect(() => {
    runtimeMonitor.logMount(componentName);
    return () => {
      runtimeMonitor.logUnmount(componentName);
    };
  }, [componentName]);

  runtimeMonitor.logRender(componentName, renderCountRef.current);
}

export default runtimeMonitor;
